/**
 * Country constants for the Student Immunization Portal
 * Includes date format preferences and embassy contact info
 */

export interface Country {
  code: string;
  name: string;
  nativeName: string;
  dateFormat: string;
  phoneCode: string;
  languages: string[];
  embassyUrl?: string;
  consulates?: ConsulateInfo[];
}

export interface ConsulateInfo {
  city: string;
  state: string;
  phone: string;
  email?: string;
  website?: string;
}

export type CountryInfo = Country;

/**
 * Top origin countries for immigrant students in Florida
 * Includes embassy/consulate info for record retrieval
 */
export const COUNTRIES: Record<string, Country> = {};

export const TOP_ORIGIN_COUNTRIES: Country[] = [
  {
    code: 'VE',
    name: 'Venezuela',
    nativeName: 'Venezuela',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+58',
    languages: ['es'],
    embassyUrl: 'https://www.embajadavenezuela.us',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 577-4214',
        email: 'consuladomiami@mppre.gob.ve',
      },
    ],
  },
  {
    code: 'HT',
    name: 'Haiti',
    nativeName: 'Ayiti',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+509',
    languages: ['ht', 'fr'],
    embassyUrl: 'https://www.haiti.org',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 859-2003',
        email: 'consulat@haitianconsulate-miami.org',
      },
      {
        city: 'Orlando',
        state: 'FL',
        phone: '(407) 872-4889',
      },
    ],
  },
  {
    code: 'BR',
    name: 'Brazil',
    nativeName: 'Brasil',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+55',
    languages: ['pt'],
    embassyUrl: 'https://www.gov.br/mre/en',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 285-6200',
        website: 'https://miami.itamaraty.gov.br',
      },
    ],
  },
  {
    code: 'CO',
    name: 'Colombia',
    nativeName: 'Colombia',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+57',
    languages: ['es'],
    embassyUrl: 'https://www.colombiaemb.org',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 448-5558',
        website: 'https://miami.consulado.gov.co',
      },
      {
        city: 'Orlando',
        state: 'FL',
        phone: '(407) 766-1070',
      },
    ],
  },
  {
    code: 'CU',
    name: 'Cuba',
    nativeName: 'Cuba',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+53',
    languages: ['es'],
    embassyUrl: 'https://misiones.cubaminrex.cu/en/usa',
    consulates: [],
  },
  {
    code: 'NI',
    name: 'Nicaragua',
    nativeName: 'Nicaragua',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+505',
    languages: ['es'],
    embassyUrl: 'https://www.consuladodenicaragua.com',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 265-1415',
      },
    ],
  },
  {
    code: 'MX',
    name: 'Mexico',
    nativeName: 'México',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+52',
    languages: ['es'],
    embassyUrl: 'https://consulmex.sre.gob.mx',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(786) 268-4900',
        website: 'https://consulmex.sre.gob.mx/miami',
      },
      {
        city: 'Orlando',
        state: 'FL',
        phone: '(407) 422-0514',
      },
    ],
  },
  {
    code: 'GT',
    name: 'Guatemala',
    nativeName: 'Guatemala',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+502',
    languages: ['es'],
    embassyUrl: 'https://www.minex.gob.gt',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 679-9945',
      },
    ],
  },
  {
    code: 'HN',
    name: 'Honduras',
    nativeName: 'Honduras',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+504',
    languages: ['es'],
    embassyUrl: 'https://www.hondurasemb.org',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 269-2832',
      },
    ],
  },
  {
    code: 'PR',
    name: 'Puerto Rico',
    nativeName: 'Puerto Rico',
    dateFormat: 'MM/dd/yyyy',
    phoneCode: '+1',
    languages: ['es', 'en'],
  },
  {
    code: 'DO',
    name: 'Dominican Republic',
    nativeName: 'República Dominicana',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+1',
    languages: ['es'],
    embassyUrl: 'https://embajadadominicana.com',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 358-3221',
      },
    ],
  },
  {
    code: 'JM',
    name: 'Jamaica',
    nativeName: 'Jamaica',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+1',
    languages: ['en'],
    embassyUrl: 'https://www.congenjamaica-ny.org',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(305) 374-8431',
      },
    ],
  },
  {
    code: 'CN',
    name: 'China',
    nativeName: '中国',
    dateFormat: 'yyyy/MM/dd',
    phoneCode: '+86',
    languages: ['zh'],
    embassyUrl: 'http://us.china-embassy.gov.cn',
    consulates: [
      {
        city: 'Houston',
        state: 'TX',
        phone: '(713) 520-1462',
        website: 'http://houston.china-consulate.gov.cn',
      },
    ],
  },
  {
    code: 'VN',
    name: 'Vietnam',
    nativeName: 'Việt Nam',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+84',
    languages: ['vi'],
    embassyUrl: 'http://vietnamembassy-usa.org',
    consulates: [
      {
        city: 'Houston',
        state: 'TX',
        phone: '(713) 850-1233',
      },
    ],
  },
  {
    code: 'IN',
    name: 'India',
    nativeName: 'भारत',
    dateFormat: 'dd/MM/yyyy',
    phoneCode: '+91',
    languages: ['en', 'hi'],
    embassyUrl: 'https://www.indiainnewyork.gov.in',
    consulates: [
      {
        city: 'Atlanta',
        state: 'GA',
        phone: '(404) 939-7203',
        website: 'https://www.cgiatlanta.gov.in',
      },
    ],
  },
  {
    code: 'PH',
    name: 'Philippines',
    nativeName: 'Pilipinas',
    dateFormat: 'MM/dd/yyyy',
    phoneCode: '+63',
    languages: ['en', 'tl'],
    embassyUrl: 'https://philippineembassy-usa.org',
    consulates: [
      {
        city: 'Miami',
        state: 'FL',
        phone: '(786) 762-2202',
      },
    ],
  },
];

// Populate COUNTRIES map
TOP_ORIGIN_COUNTRIES.forEach(country => {
  COUNTRIES[country.code] = country;
});

/**
 * Comprehensive list of countries for selection
 * Sorted alphabetically by native name
 */
export const ALL_COUNTRIES_LIST = [
  { code: "AF", nativeName: "Afġānistān" },
  { code: "AL", nativeName: "Shqipëria" },
  { code: "DZ", nativeName: "الجزائر" },
  { code: "AS", nativeName: "American Samoa" },
  { code: "AD", nativeName: "Shqipëria" },
  { code: "AO", nativeName: "Angola" },
  { code: "AI", nativeName: "Anguilla" },
  { code: "AQ", nativeName: "Andorra" },
  { code: "AG", nativeName: "Antigua and Barbuda" },
  { code: "AR", nativeName: "Argentina" },
  { code: "AM", nativeName: "Hayastan" },
  { code: "AW", nativeName: "Aruba" },
  { code: "AU", nativeName: "Australia" },
  { code: "AT", nativeName: "Österreich" },
  { code: "AZ", nativeName: "Azərbaycan" },
  { code: "BS", nativeName: "Bahamas" },
  { code: "BH", nativeName: "Bahamas" },
  { code: "BD", nativeName: "Bangladesh" },
  { code: "BB", nativeName: "Barbados" },
  { code: "BY", nativeName: "Belarus" },
  { code: "BE", nativeName: "België" },
  { code: "BZ", nativeName: "Belize" },
  { code: "BJ", nativeName: "Bénin" },
  { code: "BM", nativeName: "Bermuda" },
  { code: "BT", nativeName: "Bhutan" },
  { code: "BO", nativeName: "Bolivia" },
  { code: "BA", nativeName: "Bosna i Hercegovina" },
  { code: "BW", nativeName: "Botswana" },
  { code: "BR", nativeName: "Brasil" },
  { code: "BN", nativeName: "Botswana" },
  { code: "BG", nativeName: "България" },
  { code: "BF", nativeName: "Burkina Faso" },
  { code: "BI", nativeName: "Burundi" },
  { code: "CV", nativeName: "Cabo Verde" },
  { code: "KH", nativeName: "Campuchia" },
  { code: "CM", nativeName: "Cameroun" },
  { code: "CA", nativeName: "Canada" },
  { code: "KY", nativeName: "Cayman Islands" },
  { code: "CF", nativeName: "République centrafricaine" },
  { code: "TD", nativeName: "Tchad" },
  { code: "CL", nativeName: "Chile" },
  { code: "CN", nativeName: "中国" },
  { code: "CO", nativeName: "Colombia" },
  { code: "KM", nativeName: "Komori" },
  { code: "CG", nativeName: "Congo" },
  { code: "CD", nativeName: "République démocratique du Congo" },
  { code: "CK", nativeName: "Cook Islands" },
  { code: "CR", nativeName: "Costa Rica" },
  { code: "CI", nativeName: "Côte d'Ivoire" },
  { code: "HR", nativeName: "Hrvatska" },
  { code: "CU", nativeName: "Cuba" },
  { code: "CW", nativeName: "Cayman Islands" },
  { code: "CY", nativeName: "Kypros" },
  { code: "CZ", nativeName: "Česká republika" },
  { code: "DK", nativeName: "Danmark" },
  { code: "DJ", nativeName: "Djibouti" },
  { code: "DM", nativeName: "Dominica" },
  { code: "DO", nativeName: "República Dominicana" },
  { code: "EC", nativeName: "Ecuador" },
  { code: "EG", nativeName: "مصر" },
  { code: "SV", nativeName: "El Salvador" },
  { code: "GQ", nativeName: "Equatorial Guinea" },
  { code: "ER", nativeName: "Eritrea" },
  { code: "EE", nativeName: "Eesti" },
  { code: "SZ", nativeName: "Eswatini" },
  { code: "ET", nativeName: "Ethiopia" },
  { code: "FK", nativeName: "Falkland Islands" },
  { code: "FO", nativeName: "Føroyar" },
  { code: "FJ", nativeName: "Fiji" },
  { code: "FI", nativeName: "Suomi" },
  { code: "FR", nativeName: "France" },
  { code: "GF", nativeName: "Guyane française" },
  { code: "PF", nativeName: "Polynésie française" },
  { code: "GA", nativeName: "Gabon" },
  { code: "GM", nativeName: "Gambia" },
  { code: "GE", nativeName: "Sakartvelo" },
  { code: "DE", nativeName: "Deutschland" },
  { code: "GH", nativeName: "Ghana" },
  { code: "GI", nativeName: "Gibraltar" },
  { code: "GR", nativeName: "Ελλάδα" },
  { code: "GL", nativeName: "Greenland" },
  { code: "GD", nativeName: "Grenada" },
  { code: "GP", nativeName: "Guadeloupe" },
  { code: "GU", nativeName: "Guam" },
  { code: "GT", nativeName: "Guatemala" },
  { code: "GG", nativeName: "Guernsey" },
  { code: "GN", nativeName: "Guinée" },
  { code: "GW", nativeName: "Guiné-Bissau" },
  { code: "GY", nativeName: "Guyana" },
  { code: "HT", nativeName: "Ayiti" },
  { code: "HM", nativeName: "Heard Island and McDonald Islands" },
  { code: "VA", nativeName: "Città del Vaticano" },
  { code: "HN", nativeName: "Honduras" },
  { code: "HK", nativeName: "Hong Kong" },
  { code: "HU", nativeName: "Magyarország" },
  { code: "IS", nativeName: "Ísland" },
  { code: "IN", nativeName: "भारत" },
  { code: "ID", nativeName: "Indonesia" },
  { code: "IR", nativeName: "Iran" },
  { code: "IQ", nativeName: "Iran" },
  { code: "IE", nativeName: "Éire" },
  { code: "IM", nativeName: "Isle of Man" },
  { code: "IL", nativeName: "Yisra'el" },
  { code: "IT", nativeName: "Italia" },
  { code: "JM", nativeName: "Jamaica" },
  { code: "JP", nativeName: "日本" },
  { code: "JE", nativeName: "Jersey" },
  { code: "JO", nativeName: "الأردن" },
  { code: "KZ", nativeName: "Qazaqstan" },
  { code: "KE", nativeName: "Kenya" },
  { code: "KI", nativeName: "Kiribati" },
  { code: "KP", nativeName: "Chosŏn" },
  { code: "KR", nativeName: "Hanguk" },
  { code: "KW", nativeName: "الكويت" },
  { code: "KG", nativeName: "Qazaqstan" },
  { code: "LA", nativeName: "Lao" },
  { code: "LV", nativeName: "Latvija" },
  { code: "LB", nativeName: "Lubnān" },
  { code: "LS", nativeName: "Lesotho" },
  { code: "LR", nativeName: "Liberia" },
  { code: "LY", nativeName: "Lībiyā" },
  { code: "LI", nativeName: "Liechtenstein" },
  { code: "LT", nativeName: "Lietuva" },
  { code: "LU", nativeName: "Luxembourg" },
  { code: "MO", nativeName: "Macau" },
  { code: "MG", nativeName: "Madagasikara" },
  { code: "MW", nativeName: "Malawi" },
  { code: "MY", nativeName: "Malaysia" },
  { code: "MV", nativeName: "Maldives" },
  { code: "ML", nativeName: "Mali" },
  { code: "MT", nativeName: "Malta" },
  { code: "MH", nativeName: "Marshall Islands" },
  { code: "MQ", nativeName: "Martinique" },
  { code: "MR", nativeName: "Mauritanie" },
  { code: "MU", nativeName: "Maurice" },
  { code: "YT", nativeName: "Mayotte" },
  { code: "MX", nativeName: "México" },
  { code: "FM", nativeName: "Micronesia" },
  { code: "MD", nativeName: "Moldova" },
  { code: "MC", nativeName: "Monaco" },
  { code: "MN", nativeName: "Mongol Uls" },
  { code: "ME", nativeName: "Crna Gora" },
  { code: "MS", nativeName: "Montserrat" },
  { code: "MA", nativeName: "Al-Maghrib" },
  { code: "MZ", nativeName: "Moçambique" },
  { code: "MM", nativeName: "Myanma" },
  { code: "NA", nativeName: "Namibia" },
  { code: "NR", nativeName: "Nauru" },
  { code: "NP", nativeName: "Nepal" },
  { code: "NL", nativeName: "Nederland" },
  { code: "NC", nativeName: "Nouvelle-Calédonie" },
  { code: "NZ", nativeName: "New Zealand" },
  { code: "NI", nativeName: "Nicaragua" },
  { code: "NE", nativeName: "Niger" },
  { code: "NG", nativeName: "Nigeria" },
  { code: "NU", nativeName: "Niue" },
  { code: "NF", nativeName: "Niue" },
  { code: "MK", nativeName: "Severna Makedonija" },
  { code: "MP", nativeName: "Northern Mariana Islands" },
  { code: "NO", nativeName: "Norge" },
  { code: "OM", nativeName: "Oman" },
  { code: "PK", nativeName: "Pakistan" },
  { code: "PW", nativeName: "Palau" },
  { code: "PS", nativeName: "Filasṭīn" },
  { code: "PA", nativeName: "Panamá" },
  { code: "PG", nativeName: "Papua New Guinea" },
  { code: "PY", nativeName: "Paraguay" },
  { code: "PE", nativeName: "Perú" },
  { code: "PH", nativeName: "Pilipinas" },
  { code: "PN", nativeName: "Pitcairn Islands" },
  { code: "PL", nativeName: "Polska" },
  { code: "PT", nativeName: "Portugal" },
  { code: "PR", nativeName: "Puerto Rico" },
  { code: "QA", nativeName: "Qatar" },
  { code: "RE", nativeName: "La Réunion" },
  { code: "RO", nativeName: "România" },
  { code: "RU", nativeName: "Россия" },
  { code: "RW", nativeName: "Rwanda" },
  { code: "BL", nativeName: "Saint-Barthélemy" },
  { code: "SH", nativeName: "Saint Helena" },
  { code: "KN", nativeName: "Saint Kitts and Nevis" },
  { code: "LC", nativeName: "Saint Lucia" },
  { code: "MF", nativeName: "Saint-Martin" },
  { code: "PM", nativeName: "Saint-Pierre-et-Miquelon" },
  { code: "VC", nativeName: "Saint Vincent and the Grenadines" },
  { code: "WS", nativeName: "Samoa" },
  { code: "SM", nativeName: "San Marino" },
  { code: "ST", nativeName: "São Tomé e Príncipe" },
  { code: "SA", nativeName: "Al-Mamlaka al-ʿArabiyya as-Suʿūdiyya" },
  { code: "SN", nativeName: "Sénégal" },
  { code: "RS", nativeName: "Srbija" },
  { code: "SC", nativeName: "Seychelles" },
  { code: "SL", nativeName: "Sierra Leone" },
  { code: "SG", nativeName: "Singapore" },
  { code: "SX", nativeName: "Sint Maarten" },
  { code: "SK", nativeName: "Slovensko" },
  { code: "SI", nativeName: "Slovenija" },
  { code: "SB", nativeName: "Solomon Islands" },
  { code: "SO", nativeName: "Soomaaliya" },
  { code: "ZA", nativeName: "South Africa" },
  { code: "GS", nativeName: "South Georgia and the South Sandwich Islands" },
  { code: "SS", nativeName: "South Sudan" },
  { code: "ES", nativeName: "España" },
  { code: "LK", nativeName: "Sri Lanka" },
  { code: "SD", nativeName: "Sudan" },
  { code: "SR", nativeName: "Suriname" },
  { code: "SJ", nativeName: "Svalbard" },
  { code: "SE", nativeName: "Sverige" },
  { code: "CH", nativeName: "Schweiz" },
  { code: "SY", nativeName: "Sūriyā" },
  { code: "TW", nativeName: "Taiwan" },
  { code: "TJ", nativeName: "Tojikistan" },
  { code: "TZ", nativeName: "Tanzania" },
  { code: "TH", nativeName: "Prathet Thai" },
  { code: "TL", nativeName: "Timor-Leste" },
  { code: "TG", nativeName: "Togo" },
  { code: "TK", nativeName: "Tokelau" },
  { code: "TO", nativeName: "Tonga" },
  { code: "TT", nativeName: "Trinidad and Tobago" },
  { code: "TN", nativeName: "Tunisia" },
  { code: "TR", nativeName: "Türkiye" },
  { code: "TM", nativeName: "Turkmenistan" },
  { code: "TC", nativeName: "Northern Mariana Islands" },
  { code: "TV", nativeName: "Tuvalu" },
  { code: "UG", nativeName: "Uganda" },
  { code: "UA", nativeName: "Ukraïna" },
  { code: "AE", nativeName: "Al-Imārāt al-ʿArabiyya al-Muttaḥida" },
  { code: "GB", nativeName: "United Kingdom" },
  { code: "US", nativeName: "United States" },
  { code: "UM", nativeName: "United States Minor Outlying Islands" },
  { code: "UY", nativeName: "Uruguay" },
  { code: "UZ", nativeName: "Oʻzbekiston" },
  { code: "VU", nativeName: "Tuvalu" },
  { code: "VE", nativeName: "Venezuela" },
  { code: "VN", nativeName: "Việt Nam" },
  { code: "VG", nativeName: "Virgin Islands" },
  { code: "VI", nativeName: "Virgin Islands" },
  { code: "WF", nativeName: "Wallis-et-Futuna" },
  { code: "EH", nativeName: "Western Sahara" },
  { code: "YE", nativeName: "Yemen" },
  { code: "ZM", nativeName: "Zambia" },
  { code: "ZW", nativeName: "Zimbabwe" }
].sort((a, b) => a.nativeName.localeCompare(b.nativeName));

/**
 * All supported countries for date format detection
 */
export const ALL_COUNTRIES: Record<string, { dateFormat: string; name: string }> = {
  US: { dateFormat: 'MM/dd/yyyy', name: 'United States' },
  GB: { dateFormat: 'dd/MM/yyyy', name: 'United Kingdom' },
  DE: { dateFormat: 'dd.MM.yyyy', name: 'Germany' },
  FR: { dateFormat: 'dd/MM/yyyy', name: 'France' },
  ES: { dateFormat: 'dd/MM/yyyy', name: 'Spain' },
  IT: { dateFormat: 'dd/MM/yyyy', name: 'Italy' },
  PT: { dateFormat: 'dd/MM/yyyy', name: 'Portugal' },
  BR: { dateFormat: 'dd/MM/yyyy', name: 'Brazil' },
  MX: { dateFormat: 'dd/MM/yyyy', name: 'Mexico' },
  AR: { dateFormat: 'dd/MM/yyyy', name: 'Argentina' },
  CO: { dateFormat: 'dd/MM/yyyy', name: 'Colombia' },
  VE: { dateFormat: 'dd/MM/yyyy', name: 'Venezuela' },
  PE: { dateFormat: 'dd/MM/yyyy', name: 'Peru' },
  CL: { dateFormat: 'dd/MM/yyyy', name: 'Chile' },
  EC: { dateFormat: 'dd/MM/yyyy', name: 'Ecuador' },
  GT: { dateFormat: 'dd/MM/yyyy', name: 'Guatemala' },
  CU: { dateFormat: 'dd/MM/yyyy', name: 'Cuba' },
  BO: { dateFormat: 'dd/MM/yyyy', name: 'Bolivia' },
  DO: { dateFormat: 'dd/MM/yyyy', name: 'Dominican Republic' },
  HN: { dateFormat: 'dd/MM/yyyy', name: 'Honduras' },
  PY: { dateFormat: 'dd/MM/yyyy', name: 'Paraguay' },
  SV: { dateFormat: 'dd/MM/yyyy', name: 'El Salvador' },
  NI: { dateFormat: 'dd/MM/yyyy', name: 'Nicaragua' },
  CR: { dateFormat: 'dd/MM/yyyy', name: 'Costa Rica' },
  PA: { dateFormat: 'dd/MM/yyyy', name: 'Panama' },
  UY: { dateFormat: 'dd/MM/yyyy', name: 'Uruguay' },
  JM: { dateFormat: 'dd/MM/yyyy', name: 'Jamaica' },
  TT: { dateFormat: 'dd/MM/yyyy', name: 'Trinidad and Tobago' },
  HT: { dateFormat: 'dd/MM/yyyy', name: 'Haiti' },
  PR: { dateFormat: 'MM/dd/yyyy', name: 'Puerto Rico' },
  CN: { dateFormat: 'yyyy/MM/dd', name: 'China' },
  JP: { dateFormat: 'yyyy/MM/dd', name: 'Japan' },
  KR: { dateFormat: 'yyyy/MM/dd', name: 'South Korea' },
  TW: { dateFormat: 'yyyy/MM/dd', name: 'Taiwan' },
  VN: { dateFormat: 'dd/MM/yyyy', name: 'Vietnam' },
  TH: { dateFormat: 'dd/MM/yyyy', name: 'Thailand' },
  PH: { dateFormat: 'MM/dd/yyyy', name: 'Philippines' },
  IN: { dateFormat: 'dd/MM/yyyy', name: 'India' },
  PK: { dateFormat: 'dd/MM/yyyy', name: 'Pakistan' },
  BD: { dateFormat: 'dd/MM/yyyy', name: 'Bangladesh' },
  RU: { dateFormat: 'dd.MM.yyyy', name: 'Russia' },
  UA: { dateFormat: 'dd.MM.yyyy', name: 'Ukraine' },
  PL: { dateFormat: 'dd.MM.yyyy', name: 'Poland' },
  EG: { dateFormat: 'dd/MM/yyyy', name: 'Egypt' },
  SA: { dateFormat: 'dd/MM/yyyy', name: 'Saudi Arabia' },
  AE: { dateFormat: 'dd/MM/yyyy', name: 'United Arab Emirates' },
  IL: { dateFormat: 'dd/MM/yyyy', name: 'Israel' },
  TR: { dateFormat: 'dd.MM.yyyy', name: 'Turkey' },
  NG: { dateFormat: 'dd/MM/yyyy', name: 'Nigeria' },
  KE: { dateFormat: 'dd/MM/yyyy', name: 'Kenya' },
  ZA: { dateFormat: 'yyyy/MM/dd', name: 'South Africa' },
  AU: { dateFormat: 'dd/MM/yyyy', name: 'Australia' },
  NZ: { dateFormat: 'dd/MM/yyyy', name: 'New Zealand' },
  CA: { dateFormat: 'yyyy-MM-dd', name: 'Canada' },
};

/**
 * Get date format for a country code
 */
export const getDateFormatForCountry = (countryCode: string): string => {
  return ALL_COUNTRIES[countryCode]?.dateFormat || 'MM/dd/yyyy';
};

/**
 * Get country by code
 */
export const getCountryByCode = (code: string): Country | undefined => {
  return TOP_ORIGIN_COUNTRIES.find(c => c.code === code);
};

/**
 * Get consulate info for a country in Florida
 */
export const getFloridaConsulate = (countryCode: string): ConsulateInfo | undefined => {
  const country = getCountryByCode(countryCode);
  return country?.consulates?.find(c => c.state === 'FL');
};

/**
 * US States for address forms
 */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'PR', name: 'Puerto Rico' },
  { code: 'VI', name: 'Virgin Islands' },
  { code: 'GU', name: 'Guam' },
] as const;

export type USStateCode = typeof US_STATES[number]['code'];